/**
 * Rooms — Excel-style table view.
 *
 * Sits alongside the existing Card grid on the Admin → Rooms page and gives
 * the operator a structured, scannable inventory of every room with all the
 * fields they care about in a single row. Designed so a super-admin can:
 *
 *   • Filter by location / department / food-order status, plus full-text
 *     search across name / short code / PIN / URL.
 *   • Sort any column by clicking the header.
 *   • Toggle food orders inline (single click).
 *   • Copy the short URL or the PIN with a single click — printers love it.
 *   • Export the visible (filtered) rows to CSV — the "review in Excel" goal.
 *   • Bulk-import new rooms from a CSV file (`name,floor,location,
 *     food_orders_enabled`). Existing rooms with the same name in the same
 *     location are skipped (idempotent re-uploads are safe).
 *   • Paginate at 100 rows per page.
 *
 * Implementation notes:
 *   - All filtering/sorting/pagination is client-side because the entire
 *     rooms list comes back in one `/api/rooms` call (tenant-scoped on the
 *     server). At our scale (low thousands of rooms per tenant) this is
 *     materially faster than round-tripping to the backend for every keystroke.
 *   - CSV parser is intentionally minimal — supports quoted fields and
 *     escaped quotes, which is enough for spreadsheet exports without
 *     pulling in a 30KB dependency.
 */
import React, { useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    MagnifyingGlass, X, Copy, ArrowsClockwise, Trash, Download, Upload, QrCode,
    CaretUp, CaretDown, CaretUpDown, CheckCircle, XCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const PAGE_SIZE = 100;

/* ---------------- CSV helpers ---------------- */

function csvEscape(v) {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes(",") || s.includes("\"") || s.includes("\n")) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

function toCSV(rows, columns) {
    const head = columns.map((c) => csvEscape(c.label)).join(",");
    const body = rows.map((r) =>
        columns.map((c) => csvEscape(typeof c.value === "function" ? c.value(r) : r[c.key])).join(",")
    ).join("\n");
    return `${head}\n${body}`;
}

/**
 * Parse a CSV string into a list of objects keyed by the header row. Handles
 * quoted fields and escaped quotes ("") inside quotes. Does not support
 * multi-line quoted values — that's overkill for our use case.
 */
function parseCSV(text) {
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length < 2) return [];
    const headers = splitCSVLine(lines[0]).map((h) => h.trim().toLowerCase());
    return lines.slice(1).map((line) => {
        const parts = splitCSVLine(line);
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (parts[i] ?? "").trim(); });
        return obj;
    });
}

function splitCSVLine(line) {
    const out = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
        const c = line[i];
        if (inQuotes) {
            if (c === '"' && line[i + 1] === '"') { cur += '"'; i += 1; }
            else if (c === '"') { inQuotes = false; }
            else { cur += c; }
        } else if (c === '"') { inQuotes = true; }
        else if (c === ",") { out.push(cur); cur = ""; }
        else { cur += c; }
    }
    out.push(cur);
    return out;
}

function downloadBlob(filename, content, mime = "text/csv") {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/* ---------------- Component ---------------- */

export default function RoomsTable({
    rooms, locations, departments, onPatchRoom, onDeleteRoom, onRegeneratePin, onReload,
}) {
    const [search, setSearch] = useState("");
    const [locFilter, setLocFilter] = useState("__all__");
    const [deptFilter, setDeptFilter] = useState("__all__");
    const [foodFilter, setFoodFilter] = useState("__all__");
    const [sort, setSort] = useState({ key: "name", dir: "asc" });
    const [page, setPage] = useState(1);
    const fileRef = useRef(null);

    const locName = (id) => (locations || []).find((l) => l.id === id)?.name || "—";
    const deptName = (id) => (departments || []).find((d) => d.id === id)?.name || "—";

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (rooms || []).filter((r) => {
            if (locFilter !== "__all__" && r.location_id !== locFilter) return false;
            if (deptFilter !== "__all__" && (r.department_id || "") !== deptFilter) return false;
            if (foodFilter === "yes" && r.food_orders_enabled === false) return false;
            if (foodFilter === "no" && r.food_orders_enabled !== false) return false;
            if (q) {
                const hay = [
                    r.name, r.short_code, r.pin, r.qr_payload,
                    r.floor, r.location, locName(r.location_id), deptName(r.department_id),
                ].filter(Boolean).join(" ").toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [rooms, search, locFilter, deptFilter, foodFilter, locations, departments]);

    const sorted = useMemo(() => {
        const arr = [...filtered];
        const { key, dir } = sort;
        const mult = dir === "asc" ? 1 : -1;
        arr.sort((a, b) => {
            const av = a[key]; const bv = b[key];
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (typeof av === "boolean" || typeof bv === "boolean") {
                return (Number(!!av) - Number(!!bv)) * mult;
            }
            return String(av).localeCompare(String(bv), undefined, { numeric: true }) * mult;
        });
        return arr;
    }, [filtered, sort]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const pageSafe = Math.min(page, totalPages);
    const paged = sorted.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

    const setSortKey = (key) => {
        setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
    };
    const SortIcon = ({ k }) => {
        if (sort.key !== k) return <CaretUpDown size={11} className="inline-block opacity-40 ml-0.5" />;
        return sort.dir === "asc"
            ? <CaretUp size={11} className="inline-block ml-0.5" />
            : <CaretDown size={11} className="inline-block ml-0.5" />;
    };

    const activeFilters = [locFilter, deptFilter, foodFilter].filter((v) => v !== "__all__").length + (search ? 1 : 0);
    const clearFilters = () => { setSearch(""); setLocFilter("__all__"); setDeptFilter("__all__"); setFoodFilter("__all__"); setPage(1); };

    /* ---------------- CSV export ---------------- */
    const exportColumns = [
        { key: "name", label: "Name" },
        { key: "floor", label: "Floor" },
        { key: "location", label: "Location label" },
        { key: "location_id", label: "Location", value: (r) => locName(r.location_id) },
        { key: "department_id", label: "Department", value: (r) => deptName(r.department_id) },
        { key: "short_code", label: "Short code" },
        { key: "pin", label: "PIN" },
        { key: "qr_payload", label: "URL" },
        { key: "food_orders_enabled", label: "Food orders", value: (r) => (r.food_orders_enabled === false ? "no" : "yes") },
        { key: "created_at", label: "Created" },
    ];
    const exportCSV = () => {
        const csv = toCSV(sorted, exportColumns);
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
        downloadBlob(`rooms-${stamp}.csv`, csv);
        toast.success(`Exported ${sorted.length} rooms`);
    };

    const downloadTemplate = () => {
        const csv = "name,floor,location,food_orders_enabled\nExample Cabin,2,HQ Wing A,yes\nExample Meeting Room,3,HQ Wing B,no\n";
        downloadBlob("rooms-import-template.csv", csv);
    };

    /* ---------------- CSV import ---------------- */
    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const text = await file.text();
            const rows = parseCSV(text);
            if (!rows.length) {
                toast.error("CSV is empty or missing a header row");
                return;
            }
            // Map and validate. We accept loose header casing.
            const items = rows.map((r) => ({
                name: (r.name || r["room name"] || "").trim(),
                floor: (r.floor || "1").toString().trim(),
                location: (r.location || "Main Office").toString().trim(),
                food_orders_enabled: !["false", "no", "off", "0"].includes(String(r.food_orders_enabled || r["food orders"] || r["food_orders"] || "yes").toLowerCase().trim()),
                // location_id and department_id are optional — only used if the
                // spreadsheet author knows the IDs (rare). The default is to
                // create rooms in the admin's current location scope.
                location_id: r.location_id || undefined,
                department_id: r.department_id || undefined,
            }));
            const blanks = items.filter((i) => !i.name).length;
            if (blanks) toast.warning(`${blanks} row(s) have no name — they'll be reported as failed`);
            const confirmed = window.confirm(`Import ${items.length} room(s) from ${file.name}?\n\nRows with duplicate names will be skipped.`);
            if (!confirmed) { fileRef.current.value = ""; return; }
            const { data } = await api.post("/rooms/bulk-import", { rooms: items });
            const lines = [
                `Created: ${data.created_count}`,
                `Skipped (duplicates): ${data.skipped_count}`,
                `Failed: ${data.failed_count}`,
            ];
            if (data.failed_count) {
                const reasons = data.failed.slice(0, 3).map((f) => `• Row ${f.row}: ${f.reason}`).join("\n");
                toast.error(lines.join(" · "), { description: reasons });
            } else {
                toast.success(lines.join(" · "));
            }
            onReload?.();
        } catch (err) {
            toast.error("Import failed", { description: err?.message?.slice(0, 100) });
        } finally {
            fileRef.current.value = "";
        }
    };

    /* ---------------- Render ---------------- */

    return (
        <Card className="bg-white dark:bg-[#0B0D10] border-gray-200 dark:border-gray-800 p-5">
            {/* Filter / action bar */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
                <div className="relative w-[220px]">
                    <Label className="text-xs">Search</Label>
                    <div className="relative mt-1">
                        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            className="pl-8 pr-8 h-9"
                            placeholder="Name / PIN / short code…"
                            data-testid="rooms-table-search"
                        />
                        {search && (
                            <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>
                <div className="w-[160px]">
                    <Label className="text-xs">Location</Label>
                    <Select value={locFilter} onValueChange={(v) => { setLocFilter(v); setPage(1); }}>
                        <SelectTrigger className="h-9 mt-1" data-testid="rooms-table-filter-location"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">All locations</SelectItem>
                            {(locations || []).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="w-[160px]">
                    <Label className="text-xs">Department</Label>
                    <Select value={deptFilter} onValueChange={(v) => { setDeptFilter(v); setPage(1); }}>
                        <SelectTrigger className="h-9 mt-1" data-testid="rooms-table-filter-dept"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">All departments</SelectItem>
                            {(departments || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="w-[130px]">
                    <Label className="text-xs">Food orders</Label>
                    <Select value={foodFilter} onValueChange={(v) => { setFoodFilter(v); setPage(1); }}>
                        <SelectTrigger className="h-9 mt-1" data-testid="rooms-table-filter-food"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">All</SelectItem>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {activeFilters > 0 && (
                    <Button variant="ghost" onClick={clearFilters} className="text-xs text-gray-600 h-9" data-testid="rooms-table-filter-clear">
                        Clear filters
                    </Button>
                )}
                <div className="flex-1" />
                <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleImport}
                    className="hidden"
                    data-testid="rooms-table-import-input"
                />
                <Button
                    variant="outline" className="h-9"
                    onClick={() => fileRef.current?.click()}
                    data-testid="rooms-table-import-btn"
                >
                    <Upload size={14} className="mr-1.5" /> Import CSV
                </Button>
                <Button
                    variant="ghost" className="h-9 text-xs text-gray-600"
                    onClick={downloadTemplate}
                    data-testid="rooms-table-template-btn"
                >
                    Template
                </Button>
                <Button
                    variant="outline" className="h-9"
                    onClick={exportCSV}
                    data-testid="rooms-table-export-btn"
                >
                    <Download size={14} className="mr-1.5" /> Export CSV
                </Button>
            </div>

            <div className="text-xs text-gray-500 mb-2 flex items-center gap-2" data-testid="rooms-table-summary">
                <span>Showing {sorted.length ? (pageSafe - 1) * PAGE_SIZE + 1 : 0}–{Math.min(pageSafe * PAGE_SIZE, sorted.length)} of {sorted.length}</span>
                {activeFilters > 0 && <span className="text-gray-400">· {activeFilters} filter{activeFilters === 1 ? "" : "s"} active</span>}
                {sorted.length !== rooms.length && <span className="text-gray-400">· {rooms.length - sorted.length} hidden</span>}
            </div>

            {/* Table */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-x-auto" data-testid="rooms-table-wrapper">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-gray-50 dark:bg-gray-900/40">
                            <Th k="name" label="Room name" sort={sort} onSort={setSortKey} />
                            <Th k="location_id" label="Location" sort={sort} onSort={setSortKey} />
                            <Th k="department_id" label="Department" sort={sort} onSort={setSortKey} />
                            <Th k="floor" label="Floor" sort={sort} onSort={setSortKey} />
                            <Th k="short_code" label="Short code" sort={sort} onSort={setSortKey} />
                            <Th k="pin" label="PIN" sort={sort} onSort={setSortKey} />
                            <Th k="qr_payload" label="URL" sort={sort} onSort={setSortKey} />
                            <Th k="food_orders_enabled" label="Food" sort={sort} onSort={setSortKey} />
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paged.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} className="text-center py-10 text-gray-400">No rooms match these filters.</TableCell>
                            </TableRow>
                        )}
                        {paged.map((r) => (
                            <TableRow key={r.id} className="text-sm" data-testid={`rooms-table-row-${r.id}`}>
                                <TableCell className="font-medium text-gray-900 dark:text-gray-100">{r.name}</TableCell>
                                <TableCell className="text-gray-600 dark:text-gray-400 text-xs">{locName(r.location_id)}</TableCell>
                                <TableCell className="text-gray-600 dark:text-gray-400 text-xs">{deptName(r.department_id)}</TableCell>
                                <TableCell className="text-gray-600 dark:text-gray-400 text-xs">{r.floor}</TableCell>
                                <TableCell>
                                    <CopyChip value={r.short_code} label={`/r/${r.short_code}`} testid={`rooms-table-${r.id}-short`} />
                                </TableCell>
                                <TableCell>
                                    <CopyChip value={r.pin} label={r.pin} mono testid={`rooms-table-${r.id}-pin`} />
                                </TableCell>
                                <TableCell className="max-w-[220px]">
                                    <a
                                        href={r.qr_payload}
                                        target="_blank" rel="noreferrer"
                                        className="text-[#0055FF] hover:underline text-xs truncate inline-block max-w-full align-middle"
                                        title={r.qr_payload}
                                    >
                                        {r.qr_payload}
                                    </a>
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-1.5">
                                        <Switch
                                            checked={r.food_orders_enabled !== false}
                                            onCheckedChange={async () => {
                                                const next = !(r.food_orders_enabled !== false);
                                                await onPatchRoom(r.id, { food_orders_enabled: next });
                                            }}
                                            data-testid={`rooms-table-${r.id}-food-toggle`}
                                        />
                                        {r.food_orders_enabled === false
                                            ? <XCircle size={14} weight="fill" className="text-gray-400" />
                                            : <CheckCircle size={14} weight="fill" className="text-emerald-500" />}
                                    </div>
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="inline-flex items-center gap-1">
                                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Show QR" onClick={() => window.dispatchEvent(new CustomEvent("rooms:view-qr", { detail: r }))} data-testid={`rooms-table-${r.id}-qr-btn`}>
                                            <QrCode size={14} />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500" title="Regenerate PIN" onClick={() => onRegeneratePin(r.id)} data-testid={`rooms-table-${r.id}-regen-btn`}>
                                            <ArrowsClockwise size={14} />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" title="Delete" onClick={() => onDeleteRoom(r.id)} data-testid={`rooms-table-${r.id}-delete-btn`}>
                                            <Trash size={14} />
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between gap-3 mt-3 text-xs text-gray-600">
                    <div>Page {pageSafe} of {totalPages}</div>
                    <div className="inline-flex items-center gap-2">
                        <Button variant="outline" size="sm" disabled={pageSafe <= 1} onClick={() => setPage(pageSafe - 1)} data-testid="rooms-table-prev">Previous</Button>
                        <Button variant="outline" size="sm" disabled={pageSafe >= totalPages} onClick={() => setPage(pageSafe + 1)} data-testid="rooms-table-next">Next</Button>
                    </div>
                </div>
            )}
        </Card>
    );
}

function Th({ k, label, sort, onSort }) {
    return (
        <TableHead
            onClick={() => onSort(k)}
            className="cursor-pointer select-none whitespace-nowrap hover:text-gray-900 dark:hover:text-gray-100"
            data-testid={`rooms-table-sort-${k}`}
        >
            {label}
            {sort.key === k
                ? (sort.dir === "asc" ? <CaretUp size={11} className="inline-block ml-1" /> : <CaretDown size={11} className="inline-block ml-1" />)
                : <CaretUpDown size={11} className="inline-block ml-1 opacity-40" />}
        </TableHead>
    );
}

function CopyChip({ value, label, mono = false, testid }) {
    const [copied, setCopied] = useState(false);
    if (!value) return <span className="text-gray-400">—</span>;
    return (
        <button
            onClick={async () => {
                try {
                    await navigator.clipboard.writeText(value);
                    setCopied(true);
                    toast.success(`${label} copied`);
                    setTimeout(() => setCopied(false), 1500);
                } catch { /* clipboard might be blocked in iframe */ }
            }}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:border-[#0055FF] hover:text-[#0055FF] transition-colors ${mono ? "font-mono" : ""}`}
            title="Click to copy"
            data-testid={testid}
        >
            <span>{label}</span>
            <Copy size={10} className={copied ? "text-emerald-500" : "opacity-50"} />
        </button>
    );
}
